import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Stepper, Typography, Card, Tag, Tabs, Button, Alert } from "@ann/web-components";
import {
  ReviewContainer,
  ReviewSubtitle,
  UsersCardContent,
  TagsContainer,
  ReviewTabsContainer,
  TabPanelContent,
  TabPanelButton,
  StepperContainer,
} from "./Review.styled";
import { INVITE_STEPS, STEP_IDS } from "../../constants/inviteSteps";
import { AssignRoleCard, GroupsCard } from "../../components";
import { AssignRolesModal, AddGroupModal } from "../../../../components";

const Review = ({
  emails = [],
  selected = [],
  assignRolesSearchValue,
  setAssignRolesSearchValue,
  selectedRoles = [],
  setSelectedRoles,
  rolesData = [],
  setRolesData,
  assignGroupsSearchValue,
  setAssignGroupsSearchValue,
  selectedGroups = [],
  setSelectedGroups,
  groupsData = [],
  setGroupsData,
  enrollmentError = null,
  isEnrolling = false,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!emails.length || !selected.length) {
      navigate("/v2/user-management/invite-user", { replace: true });
    }
  }, [emails.length, selected.length, navigate]);
  const [isAssignRolesModalOpen, setIsAssignRolesModalOpen] = useState(false);
  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
  const [pendingSelectedRoles, setPendingSelectedRoles] = useState(selectedRoles);
  const [pendingSelectedGroups, setPendingSelectedGroups] = useState(selectedGroups);

  const handleOpenAssignRolesModal = () => {
    setPendingSelectedRoles(selectedRoles);
    setIsAssignRolesModalOpen(true);
  };

  const handleCloseAssignRolesModal = () => {
    setIsAssignRolesModalOpen(false);
  };

  const handleConfirmRolesSelection = (selectedRoleIds, rolesDataFromModal) => {
    setSelectedRoles(selectedRoleIds || pendingSelectedRoles);
    if (rolesDataFromModal) {
      setRolesData(rolesDataFromModal);
    }
  };

  const handleOpenAddGroupModal = () => {
    setPendingSelectedGroups(selectedGroups);
    setIsAddGroupModalOpen(true);
  };

  const handleCloseAddGroupModal = () => {
    setIsAddGroupModalOpen(false);
  };

  const handleConfirmGroupsSelection = (selectedGroupIds, groupsDataFromModal) => {
    setSelectedGroups(selectedGroupIds || pendingSelectedGroups);
    if (groupsDataFromModal) {
      setGroupsData(groupsDataFromModal);
    }
  };

  const handleRemoveRole = (roleId) => {
    setSelectedRoles((current) => current.filter((id) => id !== roleId));
  };

  const handleRemoveGroup = (groupId) => {
    setSelectedGroups((current) => current.filter((id) => id !== groupId));
  };

  return (
    <ReviewContainer>
      <StepperContainer>
        <Stepper
          activeStep={STEP_IDS.REVIEW}
          onSelectStep={() => {}}
          steps={INVITE_STEPS}
        />
      </StepperContainer>
      <Typography category="title" size="small" weight="bold">
        Review and send
      </Typography>
      <ReviewSubtitle>
        Check the details below, then send the invites. You can edit permissions later if needed.
      </ReviewSubtitle>
      {enrollmentError && (
        <Alert
          type="error"
          title="Error sending invites"
          message={enrollmentError}
          onClose={() => {}}
        />
      )}
      <Card size="medium">
        <UsersCardContent>
          <Typography category="title" size="small" weight="bold">
            Users to invite
          </Typography>
          <TagsContainer>
            {emails.map((email) => (
              <Tag key={email} closable text={email} size="small" color="informative" />
            ))}
          </TagsContainer>
        </UsersCardContent>
      </Card>
      <ReviewTabsContainer>
        <Tabs defaultIndex={0} size="medium">
          <Tabs.List>
            <Tabs.Tab index={0}>
              Roles
            </Tabs.Tab>
            <Tabs.Tab index={1}>
              Groups
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel index={0}>
            <TabPanelContent>
              <TabPanelButton>
                <Button
                  iconLeft="plus"
                  label="Add Role"
                  onClick={handleOpenAssignRolesModal}
                  size="small"
                  type="secondary"
                />
              </TabPanelButton>
              <AssignRoleCard
                onAssignRole={handleOpenAssignRolesModal}
                selectedRoles={selectedRoles}
                onRemoveRole={handleRemoveRole}
                rolesData={rolesData}
              />
            </TabPanelContent>
          </Tabs.Panel>
          <Tabs.Panel index={1}>
            <TabPanelContent>
              <TabPanelButton>
                <Button
                  iconLeft="plus"
                  label="Add group"
                  onClick={handleOpenAddGroupModal}
                  size="small"
                  type="secondary"
                />
              </TabPanelButton>
              <GroupsCard
                onAddGroups={handleOpenAddGroupModal}
                selectedGroups={selectedGroups}
                onRemoveGroup={handleRemoveGroup}
                groupsData={groupsData}
              />
            </TabPanelContent>
          </Tabs.Panel>
        </Tabs>
      </ReviewTabsContainer>
      <AssignRolesModal
        isOpen={isAssignRolesModalOpen}
        onClose={handleCloseAssignRolesModal}
        searchValue={assignRolesSearchValue}
        setSearchValue={setAssignRolesSearchValue}
        selectedRoles={pendingSelectedRoles}
        setSelectedRoles={setPendingSelectedRoles}
        onConfirmSelection={handleConfirmRolesSelection}
      />
      <AddGroupModal
        isOpen={isAddGroupModalOpen}
        onClose={handleCloseAddGroupModal}
        searchValue={assignGroupsSearchValue}
        setSearchValue={setAssignGroupsSearchValue}
        selectedGroups={pendingSelectedGroups}
        setSelectedGroups={setPendingSelectedGroups}
        onConfirmSelection={handleConfirmGroupsSelection}
      />
    </ReviewContainer>
  );
};

export default Review;

